import React, { useEffect, useState } from 'react';
import NavBar from 'src/containers/NavBar';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import Card from 'src/components/Card';
import Flex from 'src/components/Flex';
import Input from 'src/components/Input';
import Checkbox from 'src/components/Checkbox';
import Button from 'src/components/Button';
import LoadingIcon from 'src/components/LoadingIcon';
import { useOrgMfaSettings, useUpdateOrgMfaSettings } from 'src/queries/useMfa';
import { OrgMfaSettings } from 'src/api/mfa';
import { ToastStoreObject } from 'src/stores/ToastStore';
import {
    getGracePeriodEndDateForDisplay,
    MFA_GRACE_PERIOD_TIME_ZONE_LABEL,
} from 'common/lib/services/index/mfa/MfaGracePeriod';
import { parseError } from 'src/utils';
import styles from './styles.module.scss';

const MFA_METHODS = [
    { id: 'email', label: 'Email' },
    { id: 'sms', label: 'Text message' },
    { id: 'totp', label: 'Authenticator app' },
];

function MfaSettings() {
    const { data: settings, isLoading, error } = useOrgMfaSettings();
    const updateSettings = useUpdateOrgMfaSettings();
    const [enabled, setEnabled] = useState(false);
    const [gracePeriodEnabled, setGracePeriodEnabled] = useState(false);
    const [gracePeriodEndDate, setGracePeriodEndDate] = useState('');
    const [allowedMethods, setAllowedMethods] = useState<string[]>([]);
    const [trustedDeviceEnabled, setTrustedDeviceEnabled] = useState(false);
    const [trustedDeviceTtlHours, setTrustedDeviceTtlHours] = useState('24');
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (settings) {
            setEnabled(settings.enabled);
            setGracePeriodEnabled(
                settings.enabled && Boolean(settings.gracePeriodEndDate || settings.gracePeriodEndDttm),
            );
            setGracePeriodEndDate(
                settings.enabled
                    ? settings.gracePeriodEndDate || getGracePeriodEndDateForDisplay(settings.gracePeriodEndDttm) || ''
                    : '',
            );
            setAllowedMethods(settings.allowedMethods || []);
            setTrustedDeviceEnabled(Boolean(settings.trustedDeviceEnabled));
            setTrustedDeviceTtlHours(`${settings.trustedDeviceTtlHours || 24}`);
        }
    }, [settings]);

    function handleEnabledChange(checked: boolean) {
        setEnabled(checked);
        if (!checked) {
            setGracePeriodEnabled(false);
            setGracePeriodEndDate('');
            setTrustedDeviceEnabled(false);
        }
    }

    function handleGracePeriodEnabledChange(checked: boolean) {
        setGracePeriodEnabled(checked);
        if (!checked) {
            setGracePeriodEndDate('');
        }
    }

    function handleMethodChange(method: string, checked: boolean) {
        setAllowedMethods((currentMethods) =>
            checked ? [...currentMethods, method] : currentMethods.filter((currentMethod) => currentMethod !== method),
        );
    }

    async function handleSave() {
        if (enabled && allowedMethods.length === 0) {
            setValidationError('Choose at least one allowed MFA method.');
            return;
        }

        if (gracePeriodEnabled && !gracePeriodEndDate) {
            setValidationError('Enter a grace period end date.');
            return;
        }
        const ttlHours = parseInt(trustedDeviceTtlHours, 10);
        if (trustedDeviceEnabled && (isNaN(ttlHours) || ttlHours < 1 || ttlHours > 720)) {
            setValidationError('Remembered device duration must be between 1 and 720 hours.');
            return;
        }

        try {
            setValidationError('');
            const data: OrgMfaSettings = {
                enabled,
                gracePeriodEndDate: enabled && gracePeriodEnabled ? gracePeriodEndDate : null,
                allowedMethods,
                trustedDeviceEnabled: enabled && trustedDeviceEnabled,
                trustedDeviceTtlHours: isNaN(ttlHours) ? 24 : ttlHours,
            };
            await updateSettings.mutateAsync(data);
            ToastStoreObject.show('MFA settings saved');
        } catch (e) {
            setValidationError(parseError(e));
        }
    }

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex self="stretch" align="center" justify="start">
                    <Flex value={1} className={styles.pageTitle}>
                        MFA Settings
                    </Flex>
                </Flex>

                {isLoading ? (
                    <Card className={styles.cardWrap}>
                        <Flex justify="center" className={styles.loading}>
                            <LoadingIcon />
                        </Flex>
                    </Card>
                ) : error || !settings ? (
                    <Card className={styles.cardWrap}>
                        <div className={styles.error}>
                            {error instanceof Error ? error.message : 'Failed to load MFA settings'}
                        </div>
                    </Card>
                ) : (
                    <Card className={styles.cardWrap} label="Organization MFA policy">
                        <div className={styles.sectionBody}>
                            <label className={styles.checkboxLabel}>
                                <Checkbox
                                    checked={enabled}
                                    onChange={(event) => handleEnabledChange(event.target.checked)}
                                />
                                Enable MFA for this organization
                            </label>

                            <div className={styles.gracePeriodSection}>
                                <label className={styles.checkboxLabel}>
                                    <Checkbox
                                        checked={gracePeriodEnabled}
                                        onChange={(event) => handleGracePeriodEnabledChange(event.target.checked)}
                                        disabled={!enabled}
                                    />
                                    Enable grace period
                                </label>
                                <div className={styles.helpText}>
                                    Users may continue without MFA through the selected date. MFA is required starting
                                    the next calendar day at 12:00 AM {MFA_GRACE_PERIOD_TIME_ZONE_LABEL}.
                                </div>

                                {gracePeriodEnabled ? (
                                    <div className={styles.gracePeriodDate}>
                                        <Input
                                            type="date"
                                            label="Grace period end date"
                                            value={gracePeriodEndDate}
                                            onChangeText={setGracePeriodEndDate}
                                            disabled={!enabled}
                                        />
                                    </div>
                                ) : null}
                            </div>

                            <div className={styles.trustedDeviceSection}>
                                <label className={styles.checkboxLabel}>
                                    <Checkbox
                                        checked={trustedDeviceEnabled}
                                        onChange={(event) => setTrustedDeviceEnabled(event.target.checked)}
                                        disabled={!enabled}
                                    />
                                    Enable remembered browsers and devices
                                </label>
                                <div className={styles.helpText}>
                                    Users can choose to remember a browser or mobile device after MFA. MFA is required
                                    again after the configured duration.
                                </div>
                                {trustedDeviceEnabled ? (
                                    <div className={styles.trustedDeviceDuration}>
                                        <Input
                                            type="number"
                                            label="Remembered device duration (hours)"
                                            value={trustedDeviceTtlHours}
                                            onChangeText={setTrustedDeviceTtlHours}
                                            disabled={!enabled}
                                        />
                                    </div>
                                ) : null}
                            </div>

                            <div className={styles.fieldGroup}>
                                <div className={styles.fieldLabel}>Allowed methods</div>
                                <div className={styles.methodList}>
                                    {MFA_METHODS.map((method) => (
                                        <label key={method.id} className={styles.checkboxLabel}>
                                            <Checkbox
                                                checked={allowedMethods.includes(method.id)}
                                                onChange={(event) =>
                                                    handleMethodChange(method.id, event.target.checked)
                                                }
                                            />
                                            {method.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {validationError ? <div className={styles.error}>{validationError}</div> : null}

                            <div className={styles.buttonRow}>
                                <Button
                                    type="primary"
                                    text="Save settings"
                                    onClick={handleSave}
                                    isLoading={updateSettings.isLoading}
                                />
                            </div>
                        </div>
                    </Card>
                )}
            </PageContent>
        </PageContainer>
    );
}

export default MfaSettings;
